import React, { useContext, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { EmergencyContext } from "../../../context/EmergencyContext";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { INSURANCE_TYPE_OPTIONS } from "../../../common/constants";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";

const EmergencyTable = ({ selectedLocation, setSelectedLocation }) => {
  const { t } = useTranslation();
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { emergencyPatients, loading, error, setLocationId, setMemberType } = useContext(EmergencyContext);
  const [selectedInsurance, setSelectedInsurance] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation, selectedInsurance]);

  const handleLocationChange = (locationId) => {
    setSelectedLocation(locationId);
    setLocationId(locationId === "" ? null : locationId);
  };

  const handleInsuranceChange = (insurance) => {
    setSelectedInsurance(insurance);
    setMemberType(insurance === "" ? null : insurance);
  };

  const handleViewPatient = (cipId, row) => {
    navigate(`/patient-info/${cipId}`, {
      state: { patientRow: row, returnPath: location.pathname },
    });
  };

  if (loading) return <Typography>{t("Main.Loading")}</Typography>;
  if (error) return <Typography color="error">Error loading data.</Typography>;

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.varCipId,
      getPatientName: (row) => row.memberFullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row.varCipId, params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: "memberId", headerName: t("Main.Emergency.Patients.MemberID"), flex: 1, minWidth: 150 },
    { field: "memberFullName", headerName: t("Main.Emergency.Patients.PatientName"), flex: 2, minWidth: 300 },
    { field: "memberEmail", headerName: t("Main.Emergency.Patients.MemberEmail"), flex: 1, minWidth: 200 },
    { field: "memberPhone", headerName: t("Main.Emergency.Patients.Phone"), flex: 1, minWidth: 200 },
    {
      field: "admitDate",
      headerName: t("Main.Emergency.Patients.AdmitDate"),
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params?.split("T")[0] || params,
    },
    { field: "primaryCareProvider", headerName: t("Main.Emergency.Patients.PCPFullName"), flex: 1, minWidth: 150 },
    { field: "medicalCenter", headerName: t("Main.Emergency.Patients.OfficeLocation"), flex: 1.5, minWidth: 200 },
    { field: "status", headerName: t("Main.Emergency.Patients.Status"), flex: 1.5, minWidth: 200 },
  ];

  return (
    <>
      {/* Filters */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, px: 1 }}>
        {/* Location Filter */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("Main.Admitted.FilterbyInsuranceLocation")}</InputLabel>
          <Select value={selectedLocation} onChange={(e) => handleLocationChange(e.target.value)}>
            <MenuItem value="">{t("Main.Discharged.DischargedPatients.LocationNames.AllLocations")}</MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Insurance Type Filter */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("Main.Admitted.FilterbyInsurance")}</InputLabel>
          <Select value={selectedInsurance} onChange={(e) => handleInsuranceChange(e.target.value)}>
            <MenuItem value="">{t("Main.Admitted.AllInsurances")}</MenuItem>
            {INSURANCE_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <div ref={tableRef} style={{ height: 500, width: "100%" }}>
        <DataGrid
          getRowId={(row) => row.emergencyId}
          rows={emergencyPatients}
          columns={columns}
          pageSize={5}
        />
      </div>
    </>
  );
};

export default EmergencyTable;
